import {useState} from "react";
import {css, useTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {backgroundColor, flexAbsoluteCenter, h100, mt, pv, textAlign} from "@pg-design/helpers-css";
import {ChevronLeftIcon, ChevronRightIcon, CloseIcon, GalleryOutlineIcon} from "@pg-design/icons";
import {Picture} from "@pg-design/picture";
import {Text} from "@pg-design/text";

import {ZIndex} from "../../../../common/app/constants/z_index";
import {ResponsiveModal} from "../../../../components/responsive_modal/ResponsiveModal";
import {IGalleryImage} from "../../types/Entry";
import {contentWrapper} from "./article_content_styles";

interface IArticleGalleryProps {
    images: IGalleryImage[];
}

type IGalleryImageFormats = IGalleryImage["images"];

const GALLERY_LIGHTBOX_HEIGHT = 844;
const GALLERY_LIGHTBOX_WIDTH = 1500;

const getGalleryPreviewSources = (images: IGalleryImageFormats, breakpoints: {xs: number; md: number}) => [
    {minWidthPX: 0, src: images.img_430x268, width: 430, height: 268},
    {minWidthPX: breakpoints.xs, src: images.img_744x463, width: 744, height: 463},
    {minWidthPX: breakpoints.md, src: images.img_840x523, width: 840, height: 523}
];

const getGalleryLightboxSources = (images: IGalleryImageFormats, breakpoints: {xs: number; md: number}) => [
    {
        minWidthPX: 0,
        src: images.g_img_1280 ?? images.img_744x463,
        width: 1280,
        height: Math.round((GALLERY_LIGHTBOX_HEIGHT / GALLERY_LIGHTBOX_WIDTH) * 1280)
    },
    {
        minWidthPX: breakpoints.xs,
        src: images.g_img_1500x844 ?? images.g_img_1280 ?? images.img_840x523,
        width: GALLERY_LIGHTBOX_WIDTH,
        height: GALLERY_LIGHTBOX_HEIGHT
    },
    {
        minWidthPX: breakpoints.md,
        src: images.g_img_1500 ?? images.g_img_1500x844 ?? images.g_img_1280 ?? images.img_840x523,
        width: GALLERY_LIGHTBOX_WIDTH,
        height: GALLERY_LIGHTBOX_HEIGHT
    }
];

const getPhotoCountLabel = (count: number) => {
    if (count === 1) {
        return "1 zdjęcie";
    }

    const lastTwoDigits = count % 100;
    const lastDigit = count % 10;
    const isFewForm = lastDigit >= 2 && lastDigit <= 4 && !(lastTwoDigits >= 12 && lastTwoDigits <= 14);

    return isFewForm ? `${count} zdjęcia` : `${count} zdjęć`;
};

export const ArticleGallery = ({images}: IArticleGalleryProps) => {
    const theme = useTheme();
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    if (images.length === 0) {
        return null;
    }

    const activeImage = activeIndex !== null ? images[activeIndex] : null;
    const hasMultipleImages = images.length > 1;

    const showPrevImage = () =>
        setActiveIndex((prevIndex) =>
            prevIndex === null ? prevIndex : (prevIndex - 1 + images.length) % images.length
        );
    const showNextImage = () =>
        setActiveIndex((prevIndex) => (prevIndex === null ? prevIndex : (prevIndex + 1) % images.length));

    const coverImage = images[0];
    const breakpoints = {
        xs: theme.breakpoints.numeric.xs,
        md: theme.breakpoints.numeric.md
    };

    return (
        <div css={contentWrapper}>
            <CoverButton type="button" onClick={() => setActiveIndex(0)}>
                <Picture
                    fit="center"
                    sources={getGalleryPreviewSources(coverImage.images, breakpoints)}
                    alt={coverImage.title}
                    loading="lazy"
                />

                <PhotoCountBadge>
                    <GalleryOutlineIcon size="2" />
                    <Text variant="info_txt_2" as="div" css={photoCountLabel}>
                        {getPhotoCountLabel(images.length)}
                    </Text>
                </PhotoCountBadge>

                <CoverOverlay>
                    <GalleryOutlineIcon size="3" fill="#fff" />
                    <Text variant="body_copy_1" as="div" css={overlayText}>
                        Otwórz galerię
                    </Text>
                </CoverOverlay>
            </CoverButton>

            <ResponsiveModal
                isOpen={activeImage !== null}
                fullScreen
                maxWidth="100%"
                onModalClose={() => setActiveIndex(null)}
                zIndex={ZIndex.MODAL}
            >
                {activeImage && (
                    <div css={lightbox}>
                        <button
                            type="button"
                            css={closeButton}
                            onClick={() => setActiveIndex(null)}
                            aria-label="Zamknij"
                        >
                            <CloseIcon size="2" fill="#fff" />
                        </button>

                        {hasMultipleImages && (
                            <button
                                type="button"
                                css={[navButton, navButtonLeft]}
                                onClick={showPrevImage}
                                aria-label="Poprzednie zdjęcie"
                            >
                                <ChevronLeftIcon size="2" fill="#fff" />
                            </button>
                        )}

                        <figure css={figure}>
                            <Picture
                                css={imageStyle}
                                sources={getGalleryLightboxSources(activeImage.images, breakpoints)}
                                alt={activeImage.title}
                            />
                        </figure>

                        {hasMultipleImages && (
                            <button
                                type="button"
                                css={[navButton, navButtonRight]}
                                onClick={showNextImage}
                                aria-label="Następne zdjęcie"
                            >
                                <ChevronRightIcon size="2" fill="#fff" />
                            </button>
                        )}

                        {hasMultipleImages && (
                            <Text variant="body_copy_2" as="div" css={counter}>
                                {(activeIndex ?? 0) + 1} / {images.length}
                            </Text>
                        )}
                    </div>
                )}
            </ResponsiveModal>
        </div>
    );
};

const PhotoCountBadge = styled.div`
    position: absolute;
    bottom: 0.8rem;
    right: 0.8rem;
    ${flexAbsoluteCenter};
    flex-direction: column;
    gap: 0;
    padding: 0.4rem;
    border-radius: 0.4rem;
    background-color: rgba(255, 255, 255, 0.85);
`;

const photoCountLabel = css`
    ${textAlign("center")};
    ${pv(1)};
    margin: 0;
    line-height: 1;
`;

const CoverOverlay = styled.div`
    position: absolute;
    inset: 0;
    ${flexAbsoluteCenter};
    flex-direction: column;
    gap: 0.2rem;
    background-color: rgba(0, 0, 0, 0);
    opacity: 0;
    transition:
        opacity 0.2s ease-in-out,
        background-color 0.2s ease-in-out;
`;

const CoverButton = styled.button`
    position: relative;
    display: block;
    width: 100%;
    padding: 0;
    border: none;
    border-radius: 0.4rem;
    overflow: hidden;
    cursor: pointer;
    aspect-ratio: 840 / 523;

    &:hover ${CoverOverlay}, &:focus-visible ${CoverOverlay} {
        opacity: 1;
        background-color: rgba(0, 0, 0, 0.45);
    }
`;

const overlayText = css`
    ${textAlign("center")};
    ${pv()};
    margin: 0;
    line-height: 1;
    color: #fff;
`;

const lightbox = css`
    position: relative;
    ${flexAbsoluteCenter};
    ${h100};
    width: 100%;
    ${backgroundColor("#000")};
`;

const figure = css`
    margin: 0;
    max-width: 100%;
    max-height: 100%;
`;

const imageStyle = css`
    img {
        max-height: 80vh;
    }
`;

const caption = css`
    ${textAlign("center")};
    ${mt(1)};
    color: #fff;
`;

const closeButton = css`
    position: absolute;
    top: 1.6rem;
    right: 1.6rem;
    z-index: 1;
    border: none;
    background: none;
    cursor: pointer;
`;

const navButton = css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    background: none;
    cursor: pointer;
    padding: 0.8rem;
`;

const navButtonLeft = css`
    left: 0.8rem;
`;

const navButtonRight = css`
    right: 0.8rem;
`;

const counter = css`
    position: absolute;
    bottom: 1.6rem;
    left: 50%;
    transform: translateX(-50%);
    color: #fff;
`;
